import { Helmet } from "react-helmet-async";

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: "website" | "article" | "product";
  structuredData?: object;
  noindex?: boolean;
  canonical?: string;
}

export const SEO = ({
  title,
  description,
  keywords,
  image = "/images/logo.png",
  url,
  type = "website",
  structuredData,
  noindex = false,
  canonical,
}: SEOProps) => {
  const fullTitle = title.includes("SnapLading")
    ? title
    : `${title} | SnapLading`;
  const fullUrl = url
    ? `https://snaplading.com${url}`
    : "https://snaplading.com";
  const fullImage = image.startsWith("http")
    ? image
    : `https://snaplading.com${image}`;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta
        name="robots"
        content={noindex ? "noindex, nofollow" : "index, follow"}
      />
      {canonical && <link rel="canonical" href={canonical} />}

      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:site_name" content="SnapLading" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImage} />
      <meta name="twitter:site" content="@snaplading" />

      {/* Additional Meta Tags */}
      <meta name="author" content="SnapLading" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />

      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

// Predefined SEO configurations for common pages
export const SEOConfigs = {
  landing: {
    title: "SnapLading - AI Powered Bill of Lading to EDI Converter",
    description:
      "AI agents perform data entry on bills of lading in seconds directly into your current TMS via EDI X12 211 (BOL), 204 (Pickup request). No custom development required!",
    keywords:
      "bill of lading, EDI, logistics, AI, automation, TMS, freight, shipping, transportation",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "SnapLading",
      description:
        "AI-powered Bill of Lading to EDI converter for logistics and transportation",
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web",
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "USD",
        description: "Free trial available",
      },
      provider: {
        "@type": "Organization",
        name: "SnapLading",
        url: "https://snaplading.com",
      },
    },
  },
  upload: {
    title: "Upload Documents - SnapLading",
    description:
      "Upload your bills of lading for AI-powered processing and EDI conversion. Fast, accurate, and secure document processing.",
    keywords: "upload, bill of lading, document processing, AI, EDI conversion",
    noindex: true, // Protected page
  },
  documents: {
    title: "My Documents - SnapLading",
    description:
      "View and manage your processed bills of lading and EDI documents. Track processing status and download results.",
    keywords: "documents, bill of lading, EDI, processing status, download",
    noindex: true, // Protected page
  },
  billing: {
    title: "Billing & Subscription - SnapLading",
    description:
      "Manage your SnapLading subscription, view billing history, and update payment information.",
    keywords: "billing, subscription, payment, pricing, plan",
    noindex: true, // Protected page
  },
  settings: {
    title: "Account Settings - SnapLading",
    description:
      "Manage your SnapLading account settings, preferences, and integration configurations.",
    keywords: "settings, account, preferences, configuration",
    noindex: true, // Protected page
  },
  profile: {
    title: "User Profile - SnapLading",
    description:
      "View and edit your SnapLading user profile and account information.",
    keywords: "profile, user, account, personal information",
    noindex: true, // Protected page
  },
  documentation: {
    title: "Documentation - SnapLading",
    description:
      "Comprehensive documentation for SnapLading API, integrations, and usage guides. Learn how to integrate with your TMS.",
    keywords:
      "documentation, API, integration, guide, tutorial, TMS integration",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "SnapLading Documentation",
      description:
        "Comprehensive documentation for SnapLading API and integrations",
      isPartOf: {
        "@type": "WebSite",
        name: "SnapLading",
        url: "https://snaplading.com",
      },
    },
  },
  careers: {
    title: "Careers - SnapLading",
    description:
      "Join the SnapLading team and help revolutionize logistics with AI-powered solutions. View open positions and career opportunities.",
    keywords: "careers, jobs, employment, logistics, AI, technology",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "SnapLading",
      url: "https://snaplading.com",
      sameAs: [
        "https://linkedin.com/company/snaplading",
        "https://twitter.com/snaplading",
      ],
    },
  },
  privacy: {
    title: "Privacy Policy - SnapLading",
    description:
      "Learn how SnapLading protects your data and privacy. Read our comprehensive privacy policy and data protection practices.",
    keywords: "privacy, policy, data protection, GDPR, security",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "Privacy Policy",
      description: "SnapLading privacy policy and data protection practices",
    },
  },
  terms: {
    title: "Terms of Service - SnapLading",
    description:
      "Read SnapLading terms of service and user agreement. Understand your rights and responsibilities when using our platform.",
    keywords: "terms, service, agreement, legal, conditions",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "Terms of Service",
      description: "SnapLading terms of service and user agreement",
    },
  },
};
